<template>
    <div
        class="l-ss-c-results-info"
        :class="{
            'l-ss-c-results-info-with-message': hasAutocompleteElements || !totalItemsCount,
            'l-ss-c-results-info-only-results': totalItemsCount && !hasAutocompleteElements && !hasTips,
            'l-ss-c-results-info-with-tips': hasTips,
            'l-ss-c-results-info-with-autocomplete': hasAutocompleteElements,
        }">
        <div class="l-ss-c-results-info-left-section">
            <div
                class="l-ss-c-results-info-header"
                v-if="message || hasAutocompleteElements">
                <span
                    v-if="message"
                    class="l-ss-c-results-info-help-text"
                    :class="{ 'l-ss-c-results-info-help-text-force-corrected': isForceCorrectedQuery }"
                    v-html="message">
                </span>
                <span
                    v-if="isForceCorrectedQuery"
                    class="l-ss-c-results-info-cancel-force-corrected-query"
                    @click="handleCancelForceCorrectedQuery">
                    {{ $t('cancelForceCorrectedQuery') }}
                </span>

                <span
                    class="l-ss-c-results-info-autocomplete-items"
                    v-if="showAutocompleteElements && hasAutocompleteElements && !isStandardSearchType">
                    <span
                        v-for="item in list"
                        :key="item"
                        class="l-ss-c-results-info-autocomplete-item"
                        @click="() => handleSearch(item)">{{ item }}
                    </span>
                </span>
            </div>

            <section-results-info-tips
                v-if="hasTips"
                :search="search"
                @handle-tags-click="$emit('handle-tags-click', $event)"
                @result-tab-click="$emit('result-tab-click', $event)"/>
        </div>

        <div
            class="l-ss-c-results-info-count"
            v-if="totalItemsCount">
            {{ foundItemsMessage }}
        </div>
    </div>
</template>

<script>
    import { computed, ref, watch } from '@vue/composition-api';
    import SectionResultsInfoTips from '@/core/components/section-results/section-results-info-tips.vue';
    import getValueFromPath from '@/core/components/offer/helpers/get-value-from-path';
    import getCharacterDiffCount from '@/core/components/offer/helpers/get-character-diff-count';

    export default {
        components: {
            SectionResultsInfoTips,
        },
        props: {
            search: {
                type: Object,
            },
            totalItemsCount: {
                type: Number,
                default: 0,
            },
            showAutocompleteElements: {
                type: Boolean,
                default: true,
            },
        },
        setup(props, { emit, root }) {
            const previousSearchQuery = ref('');
            const currentQuery = ref('');

            const hasAutocompleteElements = computed(() => {
                return props.search
                    && props.search.suggestions
                    && !!props.search.suggestions.length
                    && !props.search.topOffersIds.length;
            });

            const list = computed(() => {
                return props.search?.suggestions || [];
            });

            const hasTips = computed(() => {
                return !!props.search?.tips?.length;
            });

            const searchType = computed(() => {
                return props.search?.meta?.searchType ? props.search?.meta?.searchType[0] : null;
            });

            const isNarrowSearchType = computed(() => {
                return searchType.value?.startsWith('narrow');
            });

            const isStandardSearchType = computed(() => {
                return searchType.value?.startsWith('standard');
            });

            const isWildcardSearchType = computed(() => {
                return searchType.value?.startsWith('wildcard');
            });

            const isForceCorrectedQuery = computed(() => {
                return props.search?.forceCorrectedQuery;
            });

            watch(() => props.search, () => {
                const controller = window['searchbooster']?.api?.controller;

                if (controller) {
                    currentQuery.value = controller.searchStateManager.state.public.query || '';
                    previousSearchQuery.value = controller.previousSearchQuery || '';
                }
            }, { immediate: true });

            const highlightDifferences = (currentQuery, previousQuery) => {
                const currentWords = currentQuery.split(' ');
                const previousWords = previousQuery.split(' ');
                const result = [];

                for (let i = 0; i < currentWords.length; i++) {
                    const currentWord = currentWords[i];
                    const previousWord = previousWords[i] || '';
                    
                    if (currentWord === previousWord) {
                        result.push(currentWord);
                    } else {
                        const highlightedWord = highlightWordDifferences(currentWord, previousWord);
                        result.push(highlightedWord);
                    }
                }

                return result.join(' ');
            };

            const highlightWordDifferences = (currentWord, previousWord) => {
                const maxLength = Math.max(currentWord.length, previousWord.length);
                let result = '';

                for (let i = 0; i < maxLength; i++) {
                    const currentChar = currentWord[i] || '';
                    const previousChar = previousWord[i] || '';

                    if (currentChar === previousChar) {
                        result += currentChar;
                    } else {
                        result += `<span style="color: red;">${currentChar}</span>`;
                    }
                }

                return result;
            };

            const message = computed(() => {
                if (isForceCorrectedQuery.value) {
                    const highlightedQuery = highlightDifferences(
                        currentQuery.value,
                        props.search.forceCorrectedQuery,
                    );
                    return root.$t('autocomplete.forceCorrectedQuery', { 0: highlightedQuery });
                }

                if (!props.totalItemsCount && !isNarrowSearchType.value) {
                    return root.$t('noResultsFoundMessage', { 0: props.search.query });
                }

                if (
                    (
                        hasAutocompleteElements.value &&
                        (isNarrowSearchType.value || isStandardSearchType.value || isWildcardSearchType.value)
                    ) || isSkuNarrowMatch()
                ) {
                    return root.$t('autocomplete.similar', { 0: props.search.query });
                }

                if (!hasAutocompleteElements.value && (isStandardSearchType.value || isWildcardSearchType.value)) {
                    return root.$t('searchType.similarSearchResultsMessage');
                }

                return '';
            });

            const foundItemsMessage = computed(() => {
                return `${root.$t('results.foundResults')}: ${props.totalItemsCount}`;
            });

            const handleSearch = (value) => {
                emit('autocomplete-search', {
                    query: value,
                });
            };

            const handleCancelForceCorrectedQuery = () => {
                const controller = window['searchbooster']?.api?.controller;
                const searchedQueriesSet = new Set(controller.searchedQueries);
                const lastSearchedQuery = Array.from(searchedQueriesSet)[searchedQueriesSet.size - 1];

                emit('autocomplete-search', {
                    query: lastSearchedQuery,
                    useForceQueryCorrection: false,
                });
            };

            const isSkuNarrowMatch = () => {
                const firstOffer = props.search?.offers?.[0];

                if (!firstOffer || !isNarrowSearchType.value) {
                    return false;
                }

                const skuPaths = root.$configuration.skuOfferPaths || [];
                const query = currentQuery.value || props.search?.query || '';

                if (!query || !skuPaths.length) {
                    return false;
                }

                for (const path of skuPaths) {
                    const skuValue = getValueFromPath(firstOffer, path);

                    if (checkSkuMatch(skuValue, query)) {
                        return true;
                    }
                }

                return false;
            };

            const checkSkuMatch = (skuValue, query) => {
                if (!skuValue) {
                    return false;
                }

                let normSku = normalizeString(skuValue);
                let normQuery = normalizeString(query);

                // Убираем ведущие нули
                normSku = normSku.replace(/^0+/, '');
                normQuery = normQuery.replace(/^0+/, '');

                if (!normSku || !normQuery) {
                    return false;
                }

                if (normSku === normQuery) {
                    return false;
                }

                const distance = getCharacterDiffCount(
                    normalizeString(normSku),
                    normalizeString(normQuery),
                );
                return distance <= 2;
            };

            // Нормализуем строку: приводим к нижнему регистру и удаляем «косметические» разделители
            // (пробелы, дефисы, подчёркивания, точки), чтобы сравнение SKU и запроса
            // было устойчивым к разным форматам ввода (например: "AB 123", "AB-123", "ab_123.").
            const normalizeString = (value) => {
                return (value || '')
                    .toString()
                    .toLowerCase()
                    .trim()
                    .replace(/[\s\-_.]/g, '');
            };

            return {
                hasAutocompleteElements,
                list,
                hasTips,
                message,
                foundItemsMessage,
                isStandardSearchType,
                isForceCorrectedQuery,
                handleSearch,
                handleCancelForceCorrectedQuery,
            };
        },
    };
</script>

<style scoped>

</style>
